import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import IconButton from '@mui/material/IconButton';
import { Box, Checkbox, TextField, Tooltip, Typography } from '@mui/material';
import { FilterListOff } from '@mui/icons-material';
import { ModalPopup, DropdownTableInput } from '_components';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { alertActions, masterActions } from '_store';
import { useDispatch, useSelector } from 'react-redux';
import { Delete } from 'images';
import { useCheckAdminRole } from '_utils/role-access';
import { ERR_MSG } from 'constants/labels';

const RoleList = ({ marketerData, data, rowSelection, options, handleChange, setSelectedRows,
    setRowSelection, handleToggleActiveStatus, isReviewer, handleRefresh, isDigitalAdmin }) => {
    const portals = useSelector((x) => x.master?.portalData);
    const header = "Role Management";
    const [selectedRow, setSelectedRow] = useState(null);
    const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
    const [openRowId, setOpenRowId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPortal, setSelectedPortal] = useState(1);
    const hasUpdateUserRolesAccess = useCheckAdminRole("UpdateUserRoles")
    const hasDeleteUserRolesAccess = useCheckAdminRole("DeleteUserRoles")

    const dispatch = useDispatch()
    const auth = useSelector(x => x.auth.value);
    const User = auth?.Data;
    const user = User?.UserDetails;
    const authUserName = `${user.firstName} ${user.lastName}`;
    const userAccess = User?.UserAccess;
    const portalId = sessionStorage.getItem('portalID');
    const portal = userAccess.find(x => x.PortalId.toString());

    const handleMultiSelectChange = (newValue, row, columnKey) => {
        handleChange(newValue, row.original, columnKey);
        setIsConfirmEnabled(true);
    };
    const handleBulkDelete = () => {
        // setConfirmDialogOpen(true);
    };

    const handleInputChange = (value, row, columnKey) => {
        handleChange(value, row.original, columnKey);
        setIsConfirmEnabled(true);
    };

    const handleOpenModal = (row) => {
        setSelectedRow(row);
        setIsModalOpen(true);
    };
    const handlePortalChange = (value) => {
        setSelectedPortal(value);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRow(null);
    };
    const handleClearFilter = () => {
        // Clear filters and sorting
        table.setSorting([]); // <-- Reset sorting
        table.resetColumnFilters(); // Optional: Reset column filters too
        setRowSelection({}); // Clear row selection
        setIsConfirmEnabled(false); // Reset confirm state if needed
    };
    const handleDelete = async (id) => {
      try {
        dispatch(alertActions.clear());
        let obj = {
          roleId: id,
          deletedBy: authUserName,
        };
        if (obj) {
          const result = await dispatch(masterActions.deleteRole(obj)).unwrap();
          if (result.Succeeded) {
            dispatch(alertActions.success({ message: result.Message, header }));
            handleRefresh();
          }
          return result.Succeeded;
        }
      } catch (error) {
        dispatch(
          alertActions.error({
            message: error.message ? ERR_MSG["073"] : error,
            header,
          }),
        );
        return false;
      }
    };
    const handleDeleteToggle = async (selectedRow) => {
      try {
        dispatch(alertActions.clear());
        const isSuccess = await handleDelete(selectedRow.RoleID);
        if (!isSuccess) throw new Error(ERR_MSG["073"]);
        setIsModalOpen(false);
        handleRefresh();
      } catch (error) {
        dispatch(
          alertActions.error({
            message: error.message ? ERR_MSG["073"] : error,
            header,
          }),
        );
      }
    };




    const columns = useMemo(() => {
        const baseColumns = [
            {
                accessorKey: 'RoleName', header: 'Role',
                Cell: ({ row, cell }) => (
                    <TextField
                        className='ServiceProvider'
                        value={cell.getValue()}
                        disabled={!rowSelection[row.id] || (isReviewer) || !hasUpdateUserRolesAccess}
                        autoFocus={false}
                        onChange={(e) => handleChange(e.target.value, row.original, 'RoleName')}

                    />
                ),
            },
            {
                accessorKey: 'PortalId',
                header: 'Portal',
                filterFn: (row, columnId, filterValue) => {
                    const portalLabel = options.find(opt => opt.value === row.getValue(columnId))?.label || '';
                    return portalLabel.toLowerCase().includes(filterValue.toLowerCase());
                },
                Cell: ({ row, cell }) => {
                    return (
                        <DropdownTableInput
                            name="selectedPortal"
                            label="Select Portal"
                            disabled={!rowSelection[row.id] || (isReviewer) || !hasUpdateUserRolesAccess}
                            value={cell.getValue()}
                            options={options}
                            onChange={(value) => handleChange(value, row.original, 'PortalId')}
                        />

                    )
                },
            },

            {
                accessorKey: 'IsWglRole',
                header: 'WGL Role',
                Cell: ({ row }) => {
                    return (
                        <Checkbox
                            disabled={!rowSelection[row.id] || isReviewer || !hasUpdateUserRolesAccess || !isDigitalAdmin}
                            color="primary"
                            checked={row.original?.IsWglRole}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                handleChange(checked, row.original, 'IsWglRole');

                                // Uncheck Customer Role if WGL Role is selected

                            }}
                        />
                    );
                },
            },

            {
                accessorKey: 'IsCustomerRole',
                header: 'Customer Role',
                Cell: ({ row }) => {
                    const isPortal4 = row.original?.PortalId === 4;
                    const isWglRole = row.original?.IsWglRole;

                    if ((isDigitalAdmin && isWglRole) || !isPortal4) {
                        return null; // Hide the checkbox completely
                    }

                    return (
                        <Checkbox
                            disabled={!rowSelection[row.id] || isReviewer || isWglRole || !hasUpdateUserRolesAccess}
                            color="primary"
                            checked={row.original?.IsCustomerRole}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                handleChange(checked, row.original, 'IsCustomerRole');
                            }}
                        />
                    );
                },
            },

        ];

        return baseColumns;
    }, [handleChange, data, selectedPortal]);
    useEffect(() => {
        // Reset openRowId when new data is loaded
        setOpenRowId(null);
    }, [data]);


    const table = useMaterialReactTable({
        columns,
        data,
        enableHiding: false,
        columnFilterDisplayMode: 'popover',
        enableFullScreenToggle: false,
        enableColumnActions: false,
        paginationDisplayMode: 'pages',
        enableStickyHeader: true,
        enableRowActions: true,
        enableRowSelection: true,
        enableExpandAll: false,
        positionExpandColumn: 'first',
        positionActionsColumn: "last",
        positionToolbarAlertBanner: 'none',
        autoResetPageIndex: false,
        state: {
            rowSelection,
        },
        getRowId: (row) => `${row.RoleID}_${row.PortalId}`, // Ensure unique IDs for rows
        onRowSelectionChange: (newRowSelection) => {
            setRowSelection(newRowSelection); // Update row selection state
        },
        muiTableHeadCellProps: {
            sx: {
                textAlign: "center",
                '&:last-of-type .MuiTableCell-head': {
                    content: '"Action"'
                }
            },
        },
        muiTableBodyCellProps: {
            sx: {
                textAlign: "center !important",
                "&   svg": {
                    fill: "#0059B3 !important", // Blue color for all checkboxes
                },
                "&   .Mui-disabled svg": {
                    fill: "#7BC1FE !important", // Dull gray for disabled checkboxes
                    cursor: "not-allowed !important", // Optional: affects hover area
                },
            },
        },
        renderEmptyRowsFallback: ({ table }) => (
            <Typography sx={{ padding: 3, textAlign: "center", fontStyle: "italic" }}>No results found for the provided search criteria.</Typography>
        ),
        displayColumnDefOptions: {
            'mrt-row-expand': {
                header: "",
                size: 10, // make the expand column wider
                muiTableHeadCellProps: {
                    sx: {
                        textAlign: "center !important",
                        float: "center !important",
                        display: 'none', // Hide the expand column

                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        textAlign: "center !important",
                        float: "center !important",
                        display: 'none', // Hide the expand column
                    },
                },
            },
        },
        initialState: {
            columnOrder: [
                'mrt-row-expand',
                'mrt-row-select',
                'ServiceProvider',
                'PortalID',
                'MarketerName',
                'StartDate',
                "UETFileID",
                'mrt-row-actions'
            ],
            sorting: [
                {
                    id: 'PortalID',
                    desc: false,
                },
                {
                    id: 'MarketerName',
                    desc: false,
                },
                {
                    id: 'StartDate',
                    desc: false,
                },
                {
                    id: 'ServiceProvider',
                    desc: false,
                },
                {
                    id: 'UETFileID',
                    desc: false,
                },

            ],
        },
        displayColumnDefOptions: {
            'mrt-row-expand': {
                header: '',
                size: 10,
                muiTableHeadCellProps: {
                    sx: {
                        display: 'none',
                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        display: 'none',
                    },
                },
            },
            'mrt-row-actions': {
                header: 'Action', // <-- Set your custom header label here
                size: 100,
                muiTableHeadCellProps: {
                    sx: {
                        textAlign: 'center',
                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        textAlign: 'center',
                    },
                },
            },
        },

        renderTopToolbarCustomActions: () => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    padding: '8px',
                    flexWrap: 'wrap',

                }}
            >
                {/* <Tooltip title="Clear Filter" className='Deactivate'>
                    <div>
                        <IconButton onClick={handleClearFilter} >
                            <FilterListOff variant="contained" color="secondary" />
                        </IconButton>
                    </div>
                </Tooltip> */}
                <Tooltip title=" Delete Selected" className='DeleteSelected'>
                    <div>
                        {/* <IconButton className='delete' onClick={handleBulkDelete} >
              <img src={Deletewhite} alt="Delete"  ></img>
            </IconButton> */}
                    </div>
                </Tooltip>
            </Box>
        ),
        renderRowActions: ({ row }) => {
            return (
                <div className='tableicons'>
                    {/* <Tooltip title={"Edit" } arrow>
                    <IconButton onClick={() => handleOpenModal(row)} className='lock'>
                        <Edit />  
                    </IconButton>
                    </Tooltip> */}

                    <IconButton className='delete'>
                        <img src={Delete} alt="Delete" onClick={() => handleOpenModal(row.original)} disabled={!hasDeleteUserRolesAccess} />
                    </IconButton>
                    {isModalOpen && selectedRow?.RoleID === row.original.RoleID && (
                        <ModalPopup
                            header={"Role Management"}
                            message1="Are you sure you want to delete the Role?"
                            btnPrimaryText="Confirm"
                            btnSecondaryText="Cancel"
                            handlePrimaryClick={() => handleDeleteToggle(row.original)}
                            handleSecondaryClick={() => handleCloseModal()}
                        />
                    )}

                </div>
            )
        },


    });

    useEffect(() => {
        const selectedFlatRows = table.getSelectedRowModel().flatRows;
        setSelectedRows(selectedFlatRows.map((row) => row.original)); // Extract original row data
    }, [rowSelection, table]); // Re-run when rowSelection changes

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MaterialReactTable table={table} />
            </LocalizationProvider>
        </>
    );
};

export default RoleList;